<template>
  <div class="with-side-panel">
    <div class="content-i">
      <div class="content-box">
        <div class="element-wrapper">

          <BackButton
              :label="$t('general.button.back')"
              :to="{ name: 'coursePrivateCoachingList' }"
              marginBottom="5"/>

          <div class="d-flex justify-content-between">
            <h6 class="element-header">
              {{ $t('course.general_title') }}
            </h6>
          </div>

          <div class="element-box">
            <h5 class="form-header">
              {{ $t('course.private_coaching.title') }}
            </h5>

            <div v-if="isLoaded">
              <form @submit.prevent="submitForm" novalidate>
                <div class="row">
                  <div class="col-12">
                    <AuditInfo
                        :user-role="currentUserRole"
                        :created-by-user-data="formData.createdByUserData"
                        :created-at="formData.createdAt"
                        :updated-by-user-data="formData.updatedByUserData"
                        :updated-at="formData.updatedAt"/>
                  </div>
                </div>
                <div class="row">
                  <div class="col-lg-6 col-md-6 col-sm-12">
                    <BaseInput
                        v-model="formData.nameOfCoaching"
                        label="Bezeichnung"
                        :required="true"
                        :validation="v$.nameOfCoaching"
                        :messages="v$.nameOfCoaching.required.$message">
                    </BaseInput>
                  </div>

                  <div class="col-lg-6 col-md-6 col-sm-12">
                    <div v-if="privateCoachingOperationTypes && privateCoachingOperationTypes.length">
                      <SelectVue
                          v-model="formData.operationType"
                          :options="operationTypeDropdown"
                          :required="true"
                          disabled="disabled"
                          label="Maßnahmetyp"
                          placeholder="Maßeinheitentyp auswählen"
                          :validation="v$.operationType"
                          :messages="v$.operationType.required.$message"/>
                    </div>
                  </div>
                </div>

                <div class="row">

                  <div class="col-lg-3 col-md-6 col-sm-12">
                    <Datepicker
                        v-model="formData.startDate"
                        :required="true"
                        label="Gültigkeitsstart"
                        :validation="v$.startDate"
                        :messages="v$.startDate.required.$message"/>
                  </div>

                  <div class="col-lg-3 col-md-6 col-sm-12">
                    <Datepicker
                        v-model="formData.endDate"
                        :required="true"
                        label="Gültigkeitsende"
                        :validation="v$.endDate"
                        :messages="v$.endDate.required.$message"/>
                  </div>
                  <div class="col-lg-6 col-md-6 col-sm-12">
                    <BaseInput
                        v-model="formData.actionNumber"
                        label="Maßnahmennummer"
                        :required="true"
                        :validation="v$.actionNumber"
                        :messages="v$.actionNumber.required.$message">
                    </BaseInput>
                  </div>
                </div>
                <div class="form-buttons-w text-center">
                  <SaveButton
                      :label="$t('general.button.save')"
                      :loading="isSubmitting"/>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <Sidebar></Sidebar>
    </div>
  </div>
</template>

<script setup>
import courseService from '@/api/Course/PrivateCoaching';
import BaseInput from '@/components/base/BaseInput.vue';
import Sidebar from '@/components/FormSidebar.vue';
import Datepicker from '@/components/picker/Datepicker.vue';
import SelectVue from '@/components/SelectVue.vue';
import {ErrorToast, SuccessToast} from '@/components/VueToast';
import router from '@/router';
import {requiredRule} from '@/shared/validators/validationRules';
import {piniaEnumsStore} from '@/store/piniaEnumsStore';
import useVuelidate from '@vuelidate/core';
import 'daterangepicker';
import 'daterangepicker/daterangepicker.css';
import {computed, onMounted, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import {useRoute} from 'vue-router';
import BackButton from "@/components/button/BackButton.vue";
import SaveButton from "@/components/button/SaveButton.vue";
import {piniaCompanySettingsStore} from "@/store/piniaCompanySettingsStore";
import AuditInfo from "@/components/AuditInfo.vue";
import {piniaUserStore} from "@/store/piniaUserStore";

const piniaCompanySettingsState = piniaCompanySettingsStore()
const companySettings = computed(() => piniaCompanySettingsState.companySettings)
const companyKey = companySettings.value.companyKey;

const piniaUserState = piniaUserStore();
const currentUserRole = computed(() => piniaUserState.currentUser?.user?.roles?.[0]);

const {t} = useI18n();
const piniaEnumsState = piniaEnumsStore();
const {privateCoachingOperationTypes} = computed(() => piniaEnumsState.enums).value;
const isSubmitting = ref(false);
const route = useRoute();
const courseId = route.params.id;
const isLoaded = ref(false);

const formData = ref({
  nameOfCoaching: '',
  operationType: 'modular_integration_coaching',
  actionNumber: '',
  startDate: '',
  endDate: '',
});

const rules = ref({
  nameOfCoaching: requiredRule(t),
  operationType: requiredRule(t),
  actionNumber: requiredRule(t),
  startDate: requiredRule(t),
  endDate: requiredRule(t),
});

const operationTypeDropdown = [
  {
    label: 'Modulares Aktivierungscoaching',
    value: 'modular_integration_coaching'
  }
]

const computedRules = computed(() => rules.value);

const v$ = useVuelidate(computedRules, formData);

async function submitForm() {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  if (isSubmitting.value) return;
  isSubmitting.value = true;

  try {
    if (courseId) {
      await courseService.bbqPutCourse(formData.value, +courseId);
    } else {
      await courseService.bbqPostCourse(formData.value);
    }

    await router.push({name: 'coursePrivateCoachingList'});

    isSubmitting.value = false;
    SuccessToast(t('general.toasts.save_success'));
  } catch (error) {
    await ErrorToast(error);
  } finally {
    isSubmitting.value = false;
  }
}

async function getCourse() {
  try {
    const {data} = await courseService.getCourse(+courseId);
    formData.value.nameOfCoaching = data.data.nameOfCoaching;
    formData.value.actionNumber = data.data.actionNumber;
    formData.value.startDate = data.data.startDate;
    formData.value.endDate = data.data.endDate;
  } catch (error) {
    await ErrorToast(error);
  }
}


onMounted(async () => {
  if(companyKey === "BSG"){
    router.push({ name: 'coursePrivateCoachingList' });
  }

  if (courseId) {
    await getCourse();
  }

  isLoaded.value = true;
});

</script>

<style scoped></style>